import React, { useState, useEffect, useMemo } from 'react'
import { defer, Outlet, redirect, useLocation, LoaderFunction } from 'react-router-dom'
// import { MpHeader } from '../shared/organism/MpHeader'
import {
  fetchBrokers,
  fetchPeopleFinderProfile,
  selectBrokers,
} from '@meprism/shared/src/redux/peopleFinder/PeopleFinderSlice'
import message from '@meprism/shared/src/assets/icons/messenger.png'
import { useInboxSocket } from '../../utils/hooks/useInboxSocket'
import { persistor, store } from '../../redux/store'
import { FetchProfile, selectProfile, ProfileSelectors } from '@meprism/shared/src/redux/profile/ProfileSlice'
import { fetchAlerts } from '@meprism/shared/src/redux/alert/AlertSlice'
import {
  checkPendingOffers,
  fetchGrants,
  fetchProducts,
} from '../../redux/product/ProductSlice'
import { Logger } from '@meprism/app-utils'
import { Box, useMediaQuery } from '@mui/material'
import { theme } from '../../theme/DashboardTheme'
import CompleteFamilyPlan from '../shared/molecule/CompleteFamilyPlanModal'
import ProfileCompletionModal from '../shared/molecule/ProfileCompletionModal'
import { useAppSelector } from '../../redux/storeExports'
import {fetchFamilyStatus, selectActiveGrant, selectFamilyStatus, fetchSubscriptionSchedule} from '@meprism/shared/src/redux/product/ProductSlice'
import { setFamilyPromptCookie } from '../../utils/helpers'
// import { MpHeader } from '../shared/organism/MpHeader'
import Cookies from 'universal-cookie'

// 3 hours in milliseconds
export const timeElapsed: number = 3 * 60 * 60 * 1000

enum ModalType {
  AUTH_LETTER = 'authLetter',
  STREET_ADDRESS = 'streetAddress',
  PHONE_NUMBER = 'phoneNumber',
  FAMILY_INVITE = 'familyInvite',
  ADDITIONAL_ADDRESSES = 'additionalAddresses',
  MIDDLE_NAME = 'middleName',
}

export const mainLayoutLoader: LoaderFunction = async ({ request }) => {
  // Wait for persist rehydration
  persistor.persist()
  if (!persistor.getState().bootstrapped) {
    await new Promise((resolve) => {
      const unsubscribe = persistor.subscribe(() => {
        if (persistor.getState().bootstrapped) {
          unsubscribe();
          resolve(true);
        }
      });
    });
  }
  const state = store.getState();
  const previousFetchTime = state.peopleFinder.previousFetchTime;
  const now = new Date().getTime();
  const user = ProfileSelectors.selectProfile(state);
  try {
    store.dispatch(fetchAlerts())
    const grants = await store.dispatch(fetchGrants()).unwrap()
    store.dispatch(fetchProducts())
    await store.dispatch(fetchSubscriptionSchedule())
    store.dispatch(checkPendingOffers())
    store.dispatch(fetchFamilyStatus())
    const peopleFinderProfile = store
      .dispatch(fetchPeopleFinderProfile())
      .unwrap()
    const brokerList = user?.scan_completed_mail_sent_at && (!previousFetchTime || (now - previousFetchTime >= timeElapsed))
      ? await store.dispatch(fetchBrokers())
      : state.peopleFinder.brokers
    const profile = await store.dispatch(FetchProfile()).unwrap()
    return defer({ 
      peopleFinderProfile, 
      brokerList, 
      profile,
      grants 
    })
  } catch (error) {
    Logger.error(`Error fetching : ${error} ${error?.message}`)
    const searchParams = new URL(request.url).search
    return redirect(`/onboarding${searchParams}`)
  }
}

export const MainLayout = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeModal, setActiveModal] = useState<ModalType | null>(null)
  const grants = useAppSelector(selectActiveGrant)
  const profile= useAppSelector(selectProfile)
  const brokerList = useAppSelector(selectBrokers)
  const location = useLocation()
  const familyPlan = useAppSelector(selectFamilyStatus)
  const shouldShowFamilyPlanModal =
    grants?.sku?.includes('fam') &&
    !familyPlan?.email &&
    location.pathname !== '/subscribe/success';
  const cookie = useMemo(() => new Cookies(), []);

  useEffect(()=>{
    if(shouldShowFamilyPlanModal){
      setFamilyPromptCookie()
    }
  },[grants, familyPlan, shouldShowFamilyPlanModal])

  const shouldHideModal = location.pathname === '/subscribe/success' || location.pathname === '/dashboard/profile'
  useEffect(() => {
    if (!shouldHideModal && !profile?.name.middle) {
        setTimeout(() => {
          setActiveModal(ModalType.MIDDLE_NAME)
        }, 1500)
      }
    else if (grants) {
      const hasEmptyStreet = profile?.address?.some(addr => !addr?.street1?.trim())
      const hasEmptyPhoneNumber = profile?.phones?.length === 0
      // Check if any address has an empty street1
      if (hasEmptyStreet) {
        if (!shouldHideModal) {
          setTimeout(() => {
            setActiveModal(ModalType.STREET_ADDRESS)
          }, 300) 
        } 
        }

      // Check if phone number is missing
      else if (hasEmptyPhoneNumber) {
        if (!shouldHideModal) {
          setTimeout(() => {
            setActiveModal(ModalType.PHONE_NUMBER)
          }, 300) 
        } 
      }
        // Then check for auth letter if street address exists
      else if (!shouldHideModal && !profile?.auth_letter_s3_key) {
        setTimeout(() => {
          setActiveModal(ModalType.AUTH_LETTER)
        }, 1500)
      }

      else if (shouldShowFamilyPlanModal) {
        const famPlanCookie = cookie.get('PromptForFamilyPlan')
        if(famPlanCookie && famPlanCookie==='true'){
          setTimeout(() => {
            setActiveModal(ModalType.FAMILY_INVITE)
          }, 1500)
        }
      }
    }

    if (!profile.name.middle && !shouldHideModal){
      setTimeout(() => {
        setActiveModal(ModalType.MIDDLE_NAME)
      }, 1500)
    }
    else if (profile.scan_completed_mail_sent_at && brokerList.length < 50){
      if (!shouldHideModal && profile.address.length < 3) {
          setTimeout(() => {
            setActiveModal(ModalType.ADDITIONAL_ADDRESSES)
          }, 300) 
        } 
    }
  },[grants, profile, shouldHideModal, shouldShowFamilyPlanModal, cookie, brokerList.length])

  useInboxSocket({ fqdn: process.env.REACT_APP_API_FQDN as string })
  const stylesbottom = {
    bottomRightIcon: {
      position: 'fixed',
      bottom: 20,
      zIndex: 9999,
      right: 20
    },
  }
  return (
    <>
      {/* <MpHeader /> */}

      <Outlet />
      {!isMobile && <Box sx={{ ...stylesbottom.bottomRightIcon, cursor: 'pointer' }}>
        <img
          src={message}
          id={'intercom-launcher'}
          alt="Description of the image"
          style={{
            maxWidth: '50px',
            maxHeight: '50px',
            marginRight: '10px',
          }}
        /></Box>}
      <ProfileCompletionModal 
        open={activeModal === ModalType.STREET_ADDRESS}
        setOpen={() => setActiveModal(null)}
        title="Street Address Required!"
        description="We need your street address to start the opt-out process"
        primaryButtonText="Add Street Address"
        redirectPath="/dashboard/profile"
        redirectState={{ state: { index: 1, fromModal: true } }}
      />
      <ProfileCompletionModal 
        open={activeModal === ModalType.PHONE_NUMBER}
        setOpen={() => setActiveModal(null)}
        title="Phone Number Required!"
        description="We need your phone number to start the opt-out process"
        primaryButtonText="Add Phone Number"
        redirectPath="/dashboard/profile"
        redirectState={{ state: { index: 2, fromModal: true } }}
      />
      <ProfileCompletionModal 
        open={activeModal === ModalType.AUTH_LETTER}
        setOpen={() => setActiveModal(null)}
        title="Authorization Required!"
        description="To proceed with the opt-out process, we need your authorization to act on your behalf"
        primaryButtonText="Sign the Authorization Letter"
        redirectPath="/authorization"
      />
      <ProfileCompletionModal
        open={activeModal === ModalType.ADDITIONAL_ADDRESSES}
        setOpen={() => setActiveModal(null)}
        title="Additional Addresses Required!"
        description="Please provide at least three addresses for more accurate scan results."
        primaryButtonText="Add Address"
        redirectPath="/dashboard/profile"
        redirectState={{ state: { index: 1, fromModal: true } }}
      />
      <ProfileCompletionModal
        open={activeModal === ModalType.MIDDLE_NAME}
        setOpen={() => setActiveModal(null)}
        title="Middle Name Required!"
        description="Please provide your middle name for more accurate scan results. If you don't have a middle name, simply enter 'NMN' to continue."
        primaryButtonText="Add Middle Name"
        redirectPath="/dashboard/profile"
        redirectState={{ state: { index: 0, fromModal: true } }}
      />
      <CompleteFamilyPlan
        open={activeModal === ModalType.FAMILY_INVITE}
        setOpen={() => setActiveModal(null)}
      />
    </>
  )
}
